/**
 * Snapshots: quantized world frames, delta compression, per-client baselines
 * and the relevancy rate gate. (§2.8)
 *
 * ── Delta on QUANTIZED values, never on floats ─────────────────────────────
 *
 * A frame stores the already-quantized integers, and the delta compares those.
 * This is the whole trick. If you delta raw f64s, a stationary player's position
 * differs by an ulp every tick, every field looks dirty, and delta compression
 * buys you nothing while costing you a mask. Quantized, a stationary player is
 * bit-identical to their baseline and costs exactly one bit.
 *
 * ── A full snapshot is a delta against an empty frame ──────────────────────
 *
 * There is no separate "write everything" path. A full snapshot deltas against
 * EMPTY_FRAME (all zeros) and the decoder starts from the same all-zero state,
 * so every field that differs from zero is sent and every field that doesn't is
 * already correct. One code path, so the full and delta paths cannot drift apart
 * — which is the usual way this bug class ships.
 *
 * ── The baseline is the CLIENT'S VIEW, not the server's truth ──────────────
 *
 * This is the subtle one, and getting it wrong produces a bug that looks like
 * "distant players freeze forever". The relevancy gate (§2.8) skips far players
 * on some frames. So after the client applies snapshot N, its world is NOT the
 * server's world at N — the skipped players are stale. If we then delta against
 * the server's truth at N, we would compute "unchanged" for a field the client
 * never received and never send it again.
 *
 * So each client's history ring stores what THAT CLIENT will hold after applying
 * each packet we sent it, and the baseline is the acked entry of that ring. The
 * ring is per-client for exactly this reason; a shared server-side ring is the
 * tempting optimization and it is wrong. `projectClientView()` below is how the
 * server advances that ring: it is defined as "decode the bytes we just sent",
 * so the server's stored baseline can never disagree with what the client holds.
 *
 * ── What a snapshot deliberately does NOT carry ────────────────────────────
 *
 * The snapshot is a RENDER + PREDICT view, not a WorldState mirror. It carries
 * what the client draws and what it needs to reconcile its own player's
 * kinematics. It omits server-side rule bookkeeping the client never renders and
 * cannot act on: charge accumulators, action/skill timers, gkHoldStartTick,
 * lastTouchTick/lastTouchedBy, the offside snapshot, and the RNG state.
 *
 * Omitting the RNG is the interesting one. It means a client re-simulating for
 * reconciliation (§2.4) draws different random numbers than the server did, so
 * its predicted shot error cone differs. That is fine and it is the documented
 * model: physics.ts's header states plainly that reconciliation CONVERGES to the
 * server rather than matching it bit-for-bit, and POSITION_EPSILON (0.05m)
 * exists to absorb exactly this. Shipping 128 bits of RNG state per snapshot to
 * chase a bit-exactness the architecture never promised would be a poor trade.
 *
 * ── Wire format (LSB-first via BitWriter; changing it is a PROTOCOL BREAK) ──
 *
 *   header: serverTick[32] seq[32] isFull[1] (baselineTick[32] if !isFull)
 *   players: dirtyMask[MAX_PLAYERS] then, per set bit i in ascending order:
 *              fieldMask[13] then each set field's value, in field-index order:
 *              flags[4] posX[16] posY[16] posZ[16] velX[16] velY[16] velZ[16]
 *              yaw[12] stamina[8] anim[6] animBlend[8] skill[4] animExec[4]
 *            (kinematic fields are only ever dirty when the flags ACTIVE bit is
 *             set, so an inactive slot costs at most its flags field)
 *   ball:  dirty[1] then fieldMask[13] + set fields, in field-index order:
 *              posX[16] posY[16] posZ[16] velX[16] velY[16] velZ[16]
 *              angX[16] angY[16] angZ[16] quat[32] possessedBy[6] grounded[1]
 *              powerKick[1]
 *   match: dirty[1] then fieldMask[16] + set fields, in field-index order:
 *              phase[4] restartStage[2] half[2] clock[20] addedTime[16]
 *              scoreA[7] scoreB[7] restartTeam[2] restartX[16] restartZ[16]
 *              restartTaker[6] possessionTeam[2] advantage[9]
 *              pendingFoulPlayer[6] pendingFoulSeverity[3] excitement[8]
 */

import type { Quat, WorldState } from '../sim/types.js';
import type { MatchPhase, RestartStage } from '../sim/types.js';
import { MAX_PLAYERS } from '../sim/types.js';
import {
  BANDWIDTH_BUDGET_DOWN_BPS,
  QUANT_POS_BITS,
  QUANT_QUAT_BITS,
  QUANT_VEL_BITS,
  SNAPSHOT_SEND_RATE,
} from '../sim/constants.js';
import { ANIM_EXEC_WIRE_BITS, animExecToken } from '../sim/anim-exec.js';
import { BitReader, BitWriter } from './bitwriter.js';
import {
  WIRE_ADDED_TIME_BITS,
  WIRE_ADVANTAGE_BITS,
  WIRE_ANGVEL_BITS,
  WIRE_ANIM_BLEND_BITS,
  WIRE_ANIM_ENUM_BITS,
  WIRE_CLOCK_BITS,
  WIRE_HALF_BITS,
  WIRE_PHASE_BITS,
  WIRE_PLAYER_REF_BITS,
  WIRE_RESTART_STAGE_BITS,
  WIRE_SCORE_BITS,
  WIRE_SEQ_BITS,
  WIRE_SEVERITY_BITS,
  WIRE_SKILL_BITS,
  WIRE_STAMINA_BITS,
  WIRE_TEAM_REF_BITS,
  WIRE_TICK_BITS,
  WIRE_UNIT_BITS,
  WIRE_YAW_BITS,
  decodePlayerRef,
  decodeTeamRef,
  dequantizeAngVel,
  dequantizeAnimBlend,
  dequantizePosX,
  dequantizePosY,
  dequantizePosZ,
  dequantizeStamina,
  dequantizeUnit,
  dequantizeVel,
  dequantizeYaw,
  encodePlayerRef,
  encodeTeamRef,
  packQuatSmallestThree,
  quantizeAngVel,
  quantizeAnimBlend,
  quantizePosX,
  quantizePosY,
  quantizePosZ,
  quantizeStamina,
  quantizeUnit,
  quantizeVel,
  quantizeYaw,
  unpackQuatSmallestThree,
} from './serialize.js';

// ---------------------------------------------------------------------------
// Field layout — indices are bit positions in the per-entity field mask.
// Order IS the protocol. Append only; never reorder.
// ---------------------------------------------------------------------------

// Per-player flags, packed into one 4-bit field.
const FLAG_ACTIVE = 1 << 0;
const FLAG_TEAM = 1 << 1;
const FLAG_POSSESSING = 1 << 2;
const FLAG_SENTOFF = 1 << 3;
const PLAYER_FLAG_BITS = 4;

const PF_FLAGS = 0;
const PF_POSX = 1;
const PF_POSY = 2;
const PF_POSZ = 3;
const PF_VELX = 4;
const PF_VELY = 5;
const PF_VELZ = 6;
const PF_YAW = 7;
const PF_STAMINA = 8;
const PF_ANIM = 9;
const PF_ANIMBLEND = 10;
const PF_SKILL = 11;
/**
 * The ACTION EXECUTION ID (§11), appended — never inserted, per the "order IS
 * the protocol" rule above. See sim/anim-exec.ts for why four bits is an
 * identity: the field is only ever compared for INEQUALITY, so it needs to
 * survive one snapshot interval, not to be globally unique.
 *
 * WHY IT HAS TO BE ON THE WIRE AT ALL. `anim` is already here, and it is a
 * LEVEL — a remote player's `AnimState.Pass` is the same value on every frame of
 * the strike window. Without this field the client cannot tell a held pass from
 * a second pass, so every consumer downstream of the seam is back to guessing:
 * dedupe on the value and the second pass never animates, dedupe on the frame
 * and the contact event fires on all of them. This is the smallest field that
 * makes "one animation start per accepted action" decidable on the client.
 */
const PF_ANIMEXEC = 12;
const PLAYER_FIELD_COUNT = 13;

const BF_POSX = 0;
const BF_POSY = 1;
const BF_POSZ = 2;
const BF_VELX = 3;
const BF_VELY = 4;
const BF_VELZ = 5;
const BF_ANGX = 6;
const BF_ANGY = 7;
const BF_ANGZ = 8;
const BF_QUAT = 9;
const BF_POSSESSED = 10;
const BF_GROUNDED = 11;
/**
 * Owner, 2026-08-15: "when it will show when kicked with full power" / "wire
 * [the wave] into online multiplayer". Mirrors `BallState.powerKick` — see
 * that field's doc for the exact trigger (boost tier 2, or full charge).
 * APPENDED, never inserted (the "order IS the protocol" rule above) — index
 * 12 was the first free slot when this landed.
 */
const BF_POWERKICK = 12;
const BALL_FIELD_COUNT = 13;

const MF_PHASE = 0;
const MF_RESTART_STAGE = 1;
const MF_HALF = 2;
const MF_CLOCK = 3;
const MF_ADDED_TIME = 4;
const MF_SCORE_A = 5;
const MF_SCORE_B = 6;
const MF_RESTART_TEAM = 7;
const MF_RESTART_X = 8;
const MF_RESTART_Z = 9;
const MF_RESTART_TAKER = 10;
const MF_POSSESSION_TEAM = 11;
const MF_ADVANTAGE = 12;
const MF_PENDING_FOUL_PLAYER = 13;
const MF_PENDING_FOUL_SEVERITY = 14;
const MF_EXCITEMENT = 15;
const MATCH_FIELD_COUNT = 16;

// ---------------------------------------------------------------------------
// SnapshotFrame — a whole world frame reduced to its quantized wire integers.
// Structure-of-arrays for the players, mirroring PlayerArrays, so quantize and
// delta are tight loops over typed arrays with zero per-call allocation.
// ---------------------------------------------------------------------------

export interface SnapshotFrame {
  tick: number;
  // Players (SoA, length MAX_PLAYERS).
  pFlags: Uint8Array;
  pPosX: Uint16Array;
  pPosY: Uint16Array;
  pPosZ: Uint16Array;
  pVelX: Uint16Array;
  pVelY: Uint16Array;
  pVelZ: Uint16Array;
  pYaw: Uint16Array;
  pStamina: Uint8Array;
  pAnim: Uint8Array;
  /** Wrapped action execution id (§11). ANIM_EXEC_WIRE_BITS wide. */
  pAnimExec: Uint8Array;
  pAnimBlend: Uint8Array;
  pSkill: Uint8Array;
  // Ball.
  bPosX: number;
  bPosY: number;
  bPosZ: number;
  bVelX: number;
  bVelY: number;
  bVelZ: number;
  bAngX: number;
  bAngY: number;
  bAngZ: number;
  bQuat: number;
  bPossessed: number;
  bGrounded: number;
  /** 0/1. Mirrors `BallState.powerKick` — see BF_POWERKICK. */
  bPowerKick: number;
  // Match.
  mPhase: number;
  mRestartStage: number;
  mHalf: number;
  mClock: number;
  mAddedTime: number;
  mScoreA: number;
  mScoreB: number;
  mRestartTeam: number;
  mRestartX: number;
  mRestartZ: number;
  mRestartTaker: number;
  mPossessionTeam: number;
  mAdvantage: number;
  mPendingFoulPlayer: number;
  mPendingFoulSeverity: number;
  mExcitement: number;
}

export function createSnapshotFrame(): SnapshotFrame {
  return {
    tick: 0,
    pFlags: new Uint8Array(MAX_PLAYERS),
    pPosX: new Uint16Array(MAX_PLAYERS),
    pPosY: new Uint16Array(MAX_PLAYERS),
    pPosZ: new Uint16Array(MAX_PLAYERS),
    pVelX: new Uint16Array(MAX_PLAYERS),
    pVelY: new Uint16Array(MAX_PLAYERS),
    pVelZ: new Uint16Array(MAX_PLAYERS),
    pYaw: new Uint16Array(MAX_PLAYERS),
    pStamina: new Uint8Array(MAX_PLAYERS),
    pAnim: new Uint8Array(MAX_PLAYERS),
    pAnimExec: new Uint8Array(MAX_PLAYERS),
    pAnimBlend: new Uint8Array(MAX_PLAYERS),
    pSkill: new Uint8Array(MAX_PLAYERS),
    bPosX: 0, bPosY: 0, bPosZ: 0,
    bVelX: 0, bVelY: 0, bVelZ: 0,
    bAngX: 0, bAngY: 0, bAngZ: 0,
    bQuat: 0, bPossessed: 0, bGrounded: 0, bPowerKick: 0,
    mPhase: 0, mRestartStage: 0, mHalf: 0,
    mClock: 0, mAddedTime: 0, mScoreA: 0, mScoreB: 0,
    mRestartTeam: 0, mRestartX: 0, mRestartZ: 0, mRestartTaker: 0,
    mPossessionTeam: 0, mAdvantage: 0,
    mPendingFoulPlayer: 0, mPendingFoulSeverity: 0, mExcitement: 0,
  };
}

/** Copy `src` over `dst` with zero allocation. */
export function copySnapshotFrame(dst: SnapshotFrame, src: SnapshotFrame): void {
  dst.tick = src.tick;
  dst.pFlags.set(src.pFlags);
  dst.pPosX.set(src.pPosX);
  dst.pPosY.set(src.pPosY);
  dst.pPosZ.set(src.pPosZ);
  dst.pVelX.set(src.pVelX);
  dst.pVelY.set(src.pVelY);
  dst.pVelZ.set(src.pVelZ);
  dst.pYaw.set(src.pYaw);
  dst.pStamina.set(src.pStamina);
  dst.pAnim.set(src.pAnim);
  dst.pAnimExec.set(src.pAnimExec);
  dst.pAnimBlend.set(src.pAnimBlend);
  dst.pSkill.set(src.pSkill);
  dst.bPosX = src.bPosX; dst.bPosY = src.bPosY; dst.bPosZ = src.bPosZ;
  dst.bVelX = src.bVelX; dst.bVelY = src.bVelY; dst.bVelZ = src.bVelZ;
  dst.bAngX = src.bAngX; dst.bAngY = src.bAngY; dst.bAngZ = src.bAngZ;
  dst.bQuat = src.bQuat; dst.bPossessed = src.bPossessed; dst.bGrounded = src.bGrounded;
  dst.bPowerKick = src.bPowerKick;
  dst.mPhase = src.mPhase; dst.mRestartStage = src.mRestartStage; dst.mHalf = src.mHalf;
  dst.mClock = src.mClock; dst.mAddedTime = src.mAddedTime;
  dst.mScoreA = src.mScoreA; dst.mScoreB = src.mScoreB;
  dst.mRestartTeam = src.mRestartTeam; dst.mRestartX = src.mRestartX; dst.mRestartZ = src.mRestartZ;
  dst.mRestartTaker = src.mRestartTaker; dst.mPossessionTeam = src.mPossessionTeam;
  dst.mAdvantage = src.mAdvantage; dst.mPendingFoulPlayer = src.mPendingFoulPlayer;
  dst.mPendingFoulSeverity = src.mPendingFoulSeverity; dst.mExcitement = src.mExcitement;
}

/** Zero a frame in place — the state a full snapshot deltas against. */
export function resetSnapshotFrame(f: SnapshotFrame): void {
  f.tick = 0;
  f.pFlags.fill(0);
  f.pPosX.fill(0); f.pPosY.fill(0); f.pPosZ.fill(0);
  f.pVelX.fill(0); f.pVelY.fill(0); f.pVelZ.fill(0);
  f.pYaw.fill(0); f.pStamina.fill(0); f.pAnim.fill(0);
  f.pAnimExec.fill(0); f.pAnimBlend.fill(0); f.pSkill.fill(0);
  f.bPosX = 0; f.bPosY = 0; f.bPosZ = 0;
  f.bVelX = 0; f.bVelY = 0; f.bVelZ = 0;
  f.bAngX = 0; f.bAngY = 0; f.bAngZ = 0;
  f.bQuat = 0; f.bPossessed = 0; f.bGrounded = 0; f.bPowerKick = 0;
  f.mPhase = 0; f.mRestartStage = 0; f.mHalf = 0;
  f.mClock = 0; f.mAddedTime = 0; f.mScoreA = 0; f.mScoreB = 0;
  f.mRestartTeam = 0; f.mRestartX = 0; f.mRestartZ = 0; f.mRestartTaker = 0;
  f.mPossessionTeam = 0; f.mAdvantage = 0;
  f.mPendingFoulPlayer = 0; f.mPendingFoulSeverity = 0; f.mExcitement = 0;
}

/**
 * The all-zero frame a FULL snapshot deltas against. Read-only by contract —
 * never mutate it; it is shared across every encode. Pass it (or use
 * `encodeFullSnapshot`) to send a keyframe.
 */
export const EMPTY_FRAME: SnapshotFrame = createSnapshotFrame();

// ---------------------------------------------------------------------------
// Quantize / dequantize — WorldState <-> SnapshotFrame. Zero allocation.
// ---------------------------------------------------------------------------

/** Clamp an integer into [0, 2^bits - 1] so a too-large counter can't wrap. */
function clampU(v: number, bits: number): number {
  const max = bits >= 32 ? 4294967295 : (1 << bits) - 1;
  if (!(v > 0)) return 0; // also pins NaN/negatives to 0
  return v > max ? max : Math.floor(v);
}

/** Fill `f` with the quantized wire integers for `w`. (§2.8) */
export function quantizeWorld(w: WorldState, f: SnapshotFrame): void {
  f.tick = w.tick;
  const p = w.players;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    f.pFlags[i] =
      (p.active[i]! & 1) |
      ((p.team[i]! & 1) << 1) |
      ((p.possessing[i]! & 1) << 2) |
      ((p.sentOff[i]! & 1) << 3);
    f.pPosX[i] = quantizePosX(p.px[i]!);
    f.pPosY[i] = quantizePosY(p.py[i]!);
    f.pPosZ[i] = quantizePosZ(p.pz[i]!);
    f.pVelX[i] = quantizeVel(p.vx[i]!);
    f.pVelY[i] = quantizeVel(p.vy[i]!);
    f.pVelZ[i] = quantizeVel(p.vz[i]!);
    f.pYaw[i] = quantizeYaw(p.yaw[i]!);
    f.pStamina[i] = quantizeStamina(p.stamina[i]!);
    f.pAnim[i] = p.anim[i]!;
    f.pAnimExec[i] = animExecToken(p.animExecId[i]!);
    f.pAnimBlend[i] = quantizeAnimBlend(p.animBlend[i]!);
    f.pSkill[i] = p.skillActive[i]!;
  }
  const b = w.ball;
  f.bPosX = quantizePosX(b.px);
  f.bPosY = quantizePosY(b.py);
  f.bPosZ = quantizePosZ(b.pz);
  f.bVelX = quantizeVel(b.vx);
  f.bVelY = quantizeVel(b.vy);
  f.bVelZ = quantizeVel(b.vz);
  f.bAngX = quantizeAngVel(b.wx);
  f.bAngY = quantizeAngVel(b.wy);
  f.bAngZ = quantizeAngVel(b.wz);
  f.bQuat = packQuatSmallestThree(b.qx, b.qy, b.qz, b.qw);
  f.bPossessed = encodePlayerRef(b.possessedBy);
  f.bGrounded = b.grounded ? 1 : 0;
  f.bPowerKick = b.powerKick ? 1 : 0;
  const m = w.match;
  f.mPhase = m.phase;
  f.mRestartStage = m.restartStage;
  f.mHalf = clampU(m.half, WIRE_HALF_BITS);
  f.mClock = clampU(m.clockTicks, WIRE_CLOCK_BITS);
  f.mAddedTime = clampU(m.addedTimeTicks, WIRE_ADDED_TIME_BITS);
  f.mScoreA = clampU(m.scoreA, WIRE_SCORE_BITS);
  f.mScoreB = clampU(m.scoreB, WIRE_SCORE_BITS);
  f.mRestartTeam = encodeTeamRef(m.restartTeam);
  f.mRestartX = quantizePosX(m.restartX);
  f.mRestartZ = quantizePosZ(m.restartZ);
  f.mRestartTaker = encodePlayerRef(m.restartTaker);
  f.mPossessionTeam = encodeTeamRef(m.possessionTeam);
  f.mAdvantage = clampU(m.advantageTicks, WIRE_ADVANTAGE_BITS);
  f.mPendingFoulPlayer = encodePlayerRef(m.pendingFoulPlayer);
  f.mPendingFoulSeverity = m.pendingFoulSeverity;
  f.mExcitement = quantizeUnit(m.excitement);
}

const scratchQuat: Quat = { x: 0, y: 0, z: 0, w: 1 };

/**
 * Write the fields a snapshot carries from `f` into `w` (dequantized). Leaves
 * everything the snapshot omits (rng, offside, config, attributes, static roles,
 * charge/timers) untouched — the client keeps its own for those. Consumers must
 * gate rendering on each player's `active` flag; an inactive slot's kinematics
 * are meaningless.
 */
export function dequantizeFrameInto(f: SnapshotFrame, w: WorldState): void {
  w.tick = f.tick;
  const p = w.players;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    const flags = f.pFlags[i]!;
    p.active[i] = flags & FLAG_ACTIVE ? 1 : 0;
    p.team[i] = flags & FLAG_TEAM ? 1 : 0;
    p.possessing[i] = flags & FLAG_POSSESSING ? 1 : 0;
    p.sentOff[i] = flags & FLAG_SENTOFF ? 1 : 0;
    p.px[i] = dequantizePosX(f.pPosX[i]!);
    p.py[i] = dequantizePosY(f.pPosY[i]!);
    p.pz[i] = dequantizePosZ(f.pPosZ[i]!);
    p.vx[i] = dequantizeVel(f.pVelX[i]!);
    p.vy[i] = dequantizeVel(f.pVelY[i]!);
    p.vz[i] = dequantizeVel(f.pVelZ[i]!);
    p.yaw[i] = dequantizeYaw(f.pYaw[i]!);
    p.stamina[i] = dequantizeStamina(f.pStamina[i]!);
    p.anim[i] = f.pAnim[i]!;
    // The wrapped token IS the client's execution id. It is never compared
    // against a locally-derived full id, only against its own previous value.
    p.animExecId[i] = f.pAnimExec[i]!;
    p.animBlend[i] = dequantizeAnimBlend(f.pAnimBlend[i]!);
    p.skillActive[i] = f.pSkill[i]!;
  }
  const b = w.ball;
  b.px = dequantizePosX(f.bPosX);
  b.py = dequantizePosY(f.bPosY);
  b.pz = dequantizePosZ(f.bPosZ);
  b.vx = dequantizeVel(f.bVelX);
  b.vy = dequantizeVel(f.bVelY);
  b.vz = dequantizeVel(f.bVelZ);
  b.wx = dequantizeAngVel(f.bAngX);
  b.wy = dequantizeAngVel(f.bAngY);
  b.wz = dequantizeAngVel(f.bAngZ);
  unpackQuatSmallestThree(f.bQuat, scratchQuat);
  b.qx = scratchQuat.x; b.qy = scratchQuat.y; b.qz = scratchQuat.z; b.qw = scratchQuat.w;
  b.possessedBy = decodePlayerRef(f.bPossessed);
  b.grounded = f.bGrounded;
  b.powerKick = f.bPowerKick === 1;
  const m = w.match;
  m.phase = f.mPhase as MatchPhase;
  m.restartStage = f.mRestartStage as RestartStage;
  m.half = f.mHalf;
  m.clockTicks = f.mClock;
  m.addedTimeTicks = f.mAddedTime;
  m.scoreA = f.mScoreA;
  m.scoreB = f.mScoreB;
  m.restartTeam = decodeTeamRef(f.mRestartTeam);
  m.restartX = dequantizePosX(f.mRestartX);
  m.restartZ = dequantizePosZ(f.mRestartZ);
  m.restartTaker = decodePlayerRef(f.mRestartTaker);
  m.possessionTeam = decodeTeamRef(f.mPossessionTeam);
  m.advantageTicks = f.mAdvantage;
  m.pendingFoulPlayer = decodePlayerRef(f.mPendingFoulPlayer);
  m.pendingFoulSeverity = f.mPendingFoulSeverity;
  m.excitement = dequantizeUnit(f.mExcitement);
}

// ---------------------------------------------------------------------------
// Delta-mask computation. A field is "changed" iff its quantized integer
// differs from the baseline's. Kinematic player fields are gated on the ACTIVE
// bit: an inactive slot's position is meaningless and must not be sent.
// ---------------------------------------------------------------------------

/** Per-player field masks for the current encode. Module scope: no per-call alloc. */
const playerFieldMasks = new Uint16Array(MAX_PLAYERS);

function playerDeltaMask(cur: SnapshotFrame, base: SnapshotFrame, i: number): number {
  let mask = 0;
  const cf = cur.pFlags[i]!;
  if (cf !== base.pFlags[i]!) mask |= 1 << PF_FLAGS;
  if (cf & FLAG_ACTIVE) {
    if (cur.pPosX[i]! !== base.pPosX[i]!) mask |= 1 << PF_POSX;
    if (cur.pPosY[i]! !== base.pPosY[i]!) mask |= 1 << PF_POSY;
    if (cur.pPosZ[i]! !== base.pPosZ[i]!) mask |= 1 << PF_POSZ;
    if (cur.pVelX[i]! !== base.pVelX[i]!) mask |= 1 << PF_VELX;
    if (cur.pVelY[i]! !== base.pVelY[i]!) mask |= 1 << PF_VELY;
    if (cur.pVelZ[i]! !== base.pVelZ[i]!) mask |= 1 << PF_VELZ;
    if (cur.pYaw[i]! !== base.pYaw[i]!) mask |= 1 << PF_YAW;
    if (cur.pStamina[i]! !== base.pStamina[i]!) mask |= 1 << PF_STAMINA;
    if (cur.pAnim[i]! !== base.pAnim[i]!) mask |= 1 << PF_ANIM;
    if (cur.pAnimExec[i]! !== base.pAnimExec[i]!) mask |= 1 << PF_ANIMEXEC;
    if (cur.pAnimBlend[i]! !== base.pAnimBlend[i]!) mask |= 1 << PF_ANIMBLEND;
    if (cur.pSkill[i]! !== base.pSkill[i]!) mask |= 1 << PF_SKILL;
  }
  return mask;
}

function ballDeltaMask(cur: SnapshotFrame, base: SnapshotFrame): number {
  let mask = 0;
  if (cur.bPosX !== base.bPosX) mask |= 1 << BF_POSX;
  if (cur.bPosY !== base.bPosY) mask |= 1 << BF_POSY;
  if (cur.bPosZ !== base.bPosZ) mask |= 1 << BF_POSZ;
  if (cur.bVelX !== base.bVelX) mask |= 1 << BF_VELX;
  if (cur.bVelY !== base.bVelY) mask |= 1 << BF_VELY;
  if (cur.bVelZ !== base.bVelZ) mask |= 1 << BF_VELZ;
  if (cur.bAngX !== base.bAngX) mask |= 1 << BF_ANGX;
  if (cur.bAngY !== base.bAngY) mask |= 1 << BF_ANGY;
  if (cur.bAngZ !== base.bAngZ) mask |= 1 << BF_ANGZ;
  if (cur.bQuat !== base.bQuat) mask |= 1 << BF_QUAT;
  if (cur.bPossessed !== base.bPossessed) mask |= 1 << BF_POSSESSED;
  if (cur.bGrounded !== base.bGrounded) mask |= 1 << BF_GROUNDED;
  if (cur.bPowerKick !== base.bPowerKick) mask |= 1 << BF_POWERKICK;
  return mask;
}

function matchDeltaMask(cur: SnapshotFrame, base: SnapshotFrame): number {
  let mask = 0;
  if (cur.mPhase !== base.mPhase) mask |= 1 << MF_PHASE;
  if (cur.mRestartStage !== base.mRestartStage) mask |= 1 << MF_RESTART_STAGE;
  if (cur.mHalf !== base.mHalf) mask |= 1 << MF_HALF;
  if (cur.mClock !== base.mClock) mask |= 1 << MF_CLOCK;
  if (cur.mAddedTime !== base.mAddedTime) mask |= 1 << MF_ADDED_TIME;
  if (cur.mScoreA !== base.mScoreA) mask |= 1 << MF_SCORE_A;
  if (cur.mScoreB !== base.mScoreB) mask |= 1 << MF_SCORE_B;
  if (cur.mRestartTeam !== base.mRestartTeam) mask |= 1 << MF_RESTART_TEAM;
  if (cur.mRestartX !== base.mRestartX) mask |= 1 << MF_RESTART_X;
  if (cur.mRestartZ !== base.mRestartZ) mask |= 1 << MF_RESTART_Z;
  if (cur.mRestartTaker !== base.mRestartTaker) mask |= 1 << MF_RESTART_TAKER;
  if (cur.mPossessionTeam !== base.mPossessionTeam) mask |= 1 << MF_POSSESSION_TEAM;
  if (cur.mAdvantage !== base.mAdvantage) mask |= 1 << MF_ADVANTAGE;
  if (cur.mPendingFoulPlayer !== base.mPendingFoulPlayer) mask |= 1 << MF_PENDING_FOUL_PLAYER;
  if (cur.mPendingFoulSeverity !== base.mPendingFoulSeverity) mask |= 1 << MF_PENDING_FOUL_SEVERITY;
  if (cur.mExcitement !== base.mExcitement) mask |= 1 << MF_EXCITEMENT;
  return mask;
}

// ---------------------------------------------------------------------------
// Encode
// ---------------------------------------------------------------------------

/**
 * Per-player eligibility for the §2.8 relevancy rate gate. `1` = the player may
 * be sent this tick if changed; `0` = force-skip (treat as unchanged, so the
 * client keeps its stale value until the next eligible tick). `null` / omitted =
 * every player eligible. This is how the server implements "far players at 10Hz".
 */
export type RelevancyMask = Uint8Array | null | undefined;

function encodeInternal(
  w: BitWriter,
  current: SnapshotFrame,
  baseline: SnapshotFrame,
  seq: number,
  isFull: boolean,
  relevancy: RelevancyMask,
): void {
  // Header.
  w.writeBits(current.tick >>> 0, WIRE_TICK_BITS);
  w.writeBits(seq >>> 0, WIRE_SEQ_BITS);
  w.writeBool(isFull);
  if (!isFull) w.writeBits(baseline.tick >>> 0, WIRE_TICK_BITS);

  // Players.
  let dirty = 0;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    const mask = !relevancy || relevancy[i] !== 0 ? playerDeltaMask(current, baseline, i) : 0;
    playerFieldMasks[i] = mask;
    if (mask !== 0) dirty |= 1 << i;
  }
  w.writeBits(dirty, MAX_PLAYERS);
  for (let i = 0; i < MAX_PLAYERS; i++) {
    const mask = playerFieldMasks[i]!;
    if (mask === 0) continue;
    w.writeBits(mask, PLAYER_FIELD_COUNT);
    if (mask & (1 << PF_FLAGS)) w.writeBits(current.pFlags[i]!, PLAYER_FLAG_BITS);
    if (mask & (1 << PF_POSX)) w.writeBits(current.pPosX[i]!, QUANT_POS_BITS);
    if (mask & (1 << PF_POSY)) w.writeBits(current.pPosY[i]!, QUANT_POS_BITS);
    if (mask & (1 << PF_POSZ)) w.writeBits(current.pPosZ[i]!, QUANT_POS_BITS);
    if (mask & (1 << PF_VELX)) w.writeBits(current.pVelX[i]!, QUANT_VEL_BITS);
    if (mask & (1 << PF_VELY)) w.writeBits(current.pVelY[i]!, QUANT_VEL_BITS);
    if (mask & (1 << PF_VELZ)) w.writeBits(current.pVelZ[i]!, QUANT_VEL_BITS);
    if (mask & (1 << PF_YAW)) w.writeBits(current.pYaw[i]!, WIRE_YAW_BITS);
    if (mask & (1 << PF_STAMINA)) w.writeBits(current.pStamina[i]!, WIRE_STAMINA_BITS);
    if (mask & (1 << PF_ANIM)) w.writeBits(current.pAnim[i]!, WIRE_ANIM_ENUM_BITS);
    if (mask & (1 << PF_ANIMBLEND)) w.writeBits(current.pAnimBlend[i]!, WIRE_ANIM_BLEND_BITS);
    if (mask & (1 << PF_SKILL)) w.writeBits(current.pSkill[i]!, WIRE_SKILL_BITS);
    if (mask & (1 << PF_ANIMEXEC)) w.writeBits(current.pAnimExec[i]!, ANIM_EXEC_WIRE_BITS);
  }

  // Ball.
  const ballMask = ballDeltaMask(current, baseline);
  w.writeBool(ballMask !== 0);
  if (ballMask !== 0) {
    w.writeBits(ballMask, BALL_FIELD_COUNT);
    if (ballMask & (1 << BF_POSX)) w.writeBits(current.bPosX, QUANT_POS_BITS);
    if (ballMask & (1 << BF_POSY)) w.writeBits(current.bPosY, QUANT_POS_BITS);
    if (ballMask & (1 << BF_POSZ)) w.writeBits(current.bPosZ, QUANT_POS_BITS);
    if (ballMask & (1 << BF_VELX)) w.writeBits(current.bVelX, QUANT_VEL_BITS);
    if (ballMask & (1 << BF_VELY)) w.writeBits(current.bVelY, QUANT_VEL_BITS);
    if (ballMask & (1 << BF_VELZ)) w.writeBits(current.bVelZ, QUANT_VEL_BITS);
    if (ballMask & (1 << BF_ANGX)) w.writeBits(current.bAngX, WIRE_ANGVEL_BITS);
    if (ballMask & (1 << BF_ANGY)) w.writeBits(current.bAngY, WIRE_ANGVEL_BITS);
    if (ballMask & (1 << BF_ANGZ)) w.writeBits(current.bAngZ, WIRE_ANGVEL_BITS);
    if (ballMask & (1 << BF_QUAT)) w.writeBits(current.bQuat, QUANT_QUAT_BITS);
    if (ballMask & (1 << BF_POSSESSED)) w.writeBits(current.bPossessed, WIRE_PLAYER_REF_BITS);
    if (ballMask & (1 << BF_GROUNDED)) w.writeBits(current.bGrounded, 1);
    if (ballMask & (1 << BF_POWERKICK)) w.writeBits(current.bPowerKick, 1);
  }

  // Match.
  const matchMask = matchDeltaMask(current, baseline);
  w.writeBool(matchMask !== 0);
  if (matchMask !== 0) {
    w.writeBits(matchMask, MATCH_FIELD_COUNT);
    if (matchMask & (1 << MF_PHASE)) w.writeBits(current.mPhase, WIRE_PHASE_BITS);
    if (matchMask & (1 << MF_RESTART_STAGE)) w.writeBits(current.mRestartStage, WIRE_RESTART_STAGE_BITS);
    if (matchMask & (1 << MF_HALF)) w.writeBits(current.mHalf, WIRE_HALF_BITS);
    if (matchMask & (1 << MF_CLOCK)) w.writeBits(current.mClock, WIRE_CLOCK_BITS);
    if (matchMask & (1 << MF_ADDED_TIME)) w.writeBits(current.mAddedTime, WIRE_ADDED_TIME_BITS);
    if (matchMask & (1 << MF_SCORE_A)) w.writeBits(current.mScoreA, WIRE_SCORE_BITS);
    if (matchMask & (1 << MF_SCORE_B)) w.writeBits(current.mScoreB, WIRE_SCORE_BITS);
    if (matchMask & (1 << MF_RESTART_TEAM)) w.writeBits(current.mRestartTeam, WIRE_TEAM_REF_BITS);
    if (matchMask & (1 << MF_RESTART_X)) w.writeBits(current.mRestartX, QUANT_POS_BITS);
    if (matchMask & (1 << MF_RESTART_Z)) w.writeBits(current.mRestartZ, QUANT_POS_BITS);
    if (matchMask & (1 << MF_RESTART_TAKER)) w.writeBits(current.mRestartTaker, WIRE_PLAYER_REF_BITS);
    if (matchMask & (1 << MF_POSSESSION_TEAM)) w.writeBits(current.mPossessionTeam, WIRE_TEAM_REF_BITS);
    if (matchMask & (1 << MF_ADVANTAGE)) w.writeBits(current.mAdvantage, WIRE_ADVANTAGE_BITS);
    if (matchMask & (1 << MF_PENDING_FOUL_PLAYER)) w.writeBits(current.mPendingFoulPlayer, WIRE_PLAYER_REF_BITS);
    if (matchMask & (1 << MF_PENDING_FOUL_SEVERITY)) w.writeBits(current.mPendingFoulSeverity, WIRE_SEVERITY_BITS);
    if (matchMask & (1 << MF_EXCITEMENT)) w.writeBits(current.mExcitement, WIRE_UNIT_BITS);
  }
}

/**
 * Encode a FULL snapshot (keyframe): delta of `current` against the all-zero
 * frame. The decoder needs no prior baseline to apply it. Send one on connect,
 * on baseline loss, or whenever a client's ack falls out of the history ring.
 */
export function encodeFullSnapshot(
  w: BitWriter,
  current: SnapshotFrame,
  seq: number,
  relevancy?: RelevancyMask,
): void {
  encodeInternal(w, current, EMPTY_FRAME, seq, true, relevancy);
}

/**
 * Encode a DELTA snapshot against `baseline` — which MUST be the frame that
 * client currently holds (see `projectClientView`). Only changed, relevant
 * fields are written.
 */
export function encodeDeltaSnapshot(
  w: BitWriter,
  current: SnapshotFrame,
  baseline: SnapshotFrame,
  seq: number,
  relevancy?: RelevancyMask,
): void {
  encodeInternal(w, current, baseline, seq, false, relevancy);
}

// ---------------------------------------------------------------------------
// Decode
// ---------------------------------------------------------------------------

export interface SnapshotHeader {
  /** Server tick this snapshot represents. */
  tick: number;
  /** Last input seq the server had processed from this client. (§2.4) */
  lastProcessedInputSeq: number;
  /** True = keyframe (self-contained); false = delta against `baseline`. */
  isFull: boolean;
  /** Tick of the baseline a delta was built against; 0 for a full snapshot. */
  baselineTick: number;
}

/**
 * Decode a snapshot into `out`, the exact inverse of encode. For a delta, pass
 * the SAME baseline the encoder used; unsent fields are inherited from it. For a
 * full snapshot the baseline is ignored (out is reset to the all-zero frame
 * first). Zero allocation: `out` is reused across calls.
 *
 * The caller should compare the returned `baselineTick` against the tick it
 * acked; a mismatch means the delta was built on a frame this client never had,
 * and the client must request a full snapshot.
 */
export function decodeSnapshot(
  r: BitReader,
  baseline: SnapshotFrame,
  out: SnapshotFrame,
): SnapshotHeader {
  const tick = r.readBits(WIRE_TICK_BITS) >>> 0;
  const seq = r.readBits(WIRE_SEQ_BITS) >>> 0;
  const isFull = r.readBool();
  const baselineTick = isFull ? 0 : r.readBits(WIRE_TICK_BITS) >>> 0;

  if (isFull) resetSnapshotFrame(out);
  else copySnapshotFrame(out, baseline);
  out.tick = tick;

  // Players — non-dirty slots keep the baseline value already in `out`.
  const dirty = r.readBits(MAX_PLAYERS);
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if ((dirty & (1 << i)) === 0) continue;
    const mask = r.readBits(PLAYER_FIELD_COUNT);
    if (mask & (1 << PF_FLAGS)) out.pFlags[i] = r.readBits(PLAYER_FLAG_BITS);
    if (mask & (1 << PF_POSX)) out.pPosX[i] = r.readBits(QUANT_POS_BITS);
    if (mask & (1 << PF_POSY)) out.pPosY[i] = r.readBits(QUANT_POS_BITS);
    if (mask & (1 << PF_POSZ)) out.pPosZ[i] = r.readBits(QUANT_POS_BITS);
    if (mask & (1 << PF_VELX)) out.pVelX[i] = r.readBits(QUANT_VEL_BITS);
    if (mask & (1 << PF_VELY)) out.pVelY[i] = r.readBits(QUANT_VEL_BITS);
    if (mask & (1 << PF_VELZ)) out.pVelZ[i] = r.readBits(QUANT_VEL_BITS);
    if (mask & (1 << PF_YAW)) out.pYaw[i] = r.readBits(WIRE_YAW_BITS);
    if (mask & (1 << PF_STAMINA)) out.pStamina[i] = r.readBits(WIRE_STAMINA_BITS);
    if (mask & (1 << PF_ANIM)) out.pAnim[i] = r.readBits(WIRE_ANIM_ENUM_BITS);
    if (mask & (1 << PF_ANIMBLEND)) out.pAnimBlend[i] = r.readBits(WIRE_ANIM_BLEND_BITS);
    if (mask & (1 << PF_SKILL)) out.pSkill[i] = r.readBits(WIRE_SKILL_BITS);
    if (mask & (1 << PF_ANIMEXEC)) out.pAnimExec[i] = r.readBits(ANIM_EXEC_WIRE_BITS);
  }

  // Ball.
  if (r.readBool()) {
    const mask = r.readBits(BALL_FIELD_COUNT);
    if (mask & (1 << BF_POSX)) out.bPosX = r.readBits(QUANT_POS_BITS);
    if (mask & (1 << BF_POSY)) out.bPosY = r.readBits(QUANT_POS_BITS);
    if (mask & (1 << BF_POSZ)) out.bPosZ = r.readBits(QUANT_POS_BITS);
    if (mask & (1 << BF_VELX)) out.bVelX = r.readBits(QUANT_VEL_BITS);
    if (mask & (1 << BF_VELY)) out.bVelY = r.readBits(QUANT_VEL_BITS);
    if (mask & (1 << BF_VELZ)) out.bVelZ = r.readBits(QUANT_VEL_BITS);
    if (mask & (1 << BF_ANGX)) out.bAngX = r.readBits(WIRE_ANGVEL_BITS);
    if (mask & (1 << BF_ANGY)) out.bAngY = r.readBits(WIRE_ANGVEL_BITS);
    if (mask & (1 << BF_ANGZ)) out.bAngZ = r.readBits(WIRE_ANGVEL_BITS);
    if (mask & (1 << BF_QUAT)) out.bQuat = r.readBits(QUANT_QUAT_BITS) >>> 0;
    if (mask & (1 << BF_POSSESSED)) out.bPossessed = r.readBits(WIRE_PLAYER_REF_BITS);
    if (mask & (1 << BF_GROUNDED)) out.bGrounded = r.readBits(1);
    if (mask & (1 << BF_POWERKICK)) out.bPowerKick = r.readBits(1);
  }

  // Match.
  if (r.readBool()) {
    const mask = r.readBits(MATCH_FIELD_COUNT);
    if (mask & (1 << MF_PHASE)) out.mPhase = r.readBits(WIRE_PHASE_BITS);
    if (mask & (1 << MF_RESTART_STAGE)) out.mRestartStage = r.readBits(WIRE_RESTART_STAGE_BITS);
    if (mask & (1 << MF_HALF)) out.mHalf = r.readBits(WIRE_HALF_BITS);
    if (mask & (1 << MF_CLOCK)) out.mClock = r.readBits(WIRE_CLOCK_BITS);
    if (mask & (1 << MF_ADDED_TIME)) out.mAddedTime = r.readBits(WIRE_ADDED_TIME_BITS);
    if (mask & (1 << MF_SCORE_A)) out.mScoreA = r.readBits(WIRE_SCORE_BITS);
    if (mask & (1 << MF_SCORE_B)) out.mScoreB = r.readBits(WIRE_SCORE_BITS);
    if (mask & (1 << MF_RESTART_TEAM)) out.mRestartTeam = r.readBits(WIRE_TEAM_REF_BITS);
    if (mask & (1 << MF_RESTART_X)) out.mRestartX = r.readBits(QUANT_POS_BITS);
    if (mask & (1 << MF_RESTART_Z)) out.mRestartZ = r.readBits(QUANT_POS_BITS);
    if (mask & (1 << MF_RESTART_TAKER)) out.mRestartTaker = r.readBits(WIRE_PLAYER_REF_BITS);
    if (mask & (1 << MF_POSSESSION_TEAM)) out.mPossessionTeam = r.readBits(WIRE_TEAM_REF_BITS);
    if (mask & (1 << MF_ADVANTAGE)) out.mAdvantage = r.readBits(WIRE_ADVANTAGE_BITS);
    if (mask & (1 << MF_PENDING_FOUL_PLAYER)) out.mPendingFoulPlayer = r.readBits(WIRE_PLAYER_REF_BITS);
    if (mask & (1 << MF_PENDING_FOUL_SEVERITY)) out.mPendingFoulSeverity = r.readBits(WIRE_SEVERITY_BITS);
    if (mask & (1 << MF_EXCITEMENT)) out.mExcitement = r.readBits(WIRE_UNIT_BITS);
  }

  return { tick, lastProcessedInputSeq: seq, isFull, baselineTick };
}

// ---------------------------------------------------------------------------
// Per-client baseline advance (§2.8 "baseline is the client's view")
// ---------------------------------------------------------------------------

/** Reused so `projectClientView` never allocates a reader. */
const projectionReader = new BitReader(new Uint8Array(1));

/**
 * Compute the frame the client will hold after applying `sentBytes`, and write
 * it into `out`. The server stores `out` as that client's new baseline. Defined
 * as "decode what we sent" so the server's baseline can never drift from the
 * client's actual state — including under relevancy skips, where the skipped
 * players correctly stay at their previous value.
 */
export function projectClientView(
  sentBytes: Uint8Array,
  baseline: SnapshotFrame,
  out: SnapshotFrame,
): SnapshotHeader {
  projectionReader.reset(sentBytes);
  return decodeSnapshot(projectionReader, baseline, out);
}

// ---------------------------------------------------------------------------
// Bandwidth budget (§2.8 — hard requirement)
// ---------------------------------------------------------------------------

/** Per-snapshot byte ceiling implied by the down budget at the snapshot rate. */
export const SNAPSHOT_BYTE_BUDGET = Math.floor(BANDWIDTH_BUDGET_DOWN_BPS / SNAPSHOT_SEND_RATE);

const worstCaseWriter = new BitWriter();
const worstCaseFrame = createSnapshotFrame();

/**
 * Measure the true worst-case snapshot size: a full snapshot in which all 22
 * players are active and every field of every entity is maximally non-baseline,
 * so nothing delta-compresses away. This is the number the §2.8 budget must
 * cover; the real, delta-compressed, rate-gated traffic is always smaller.
 */
export function measureWorstCaseSnapshotBytes(): number {
  const f = worstCaseFrame;
  f.tick = 0xffffffff >>> 0;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    f.pFlags[i] = FLAG_ACTIVE | FLAG_TEAM | FLAG_POSSESSING | FLAG_SENTOFF;
    f.pPosX[i] = 0xffff; f.pPosY[i] = 0xffff; f.pPosZ[i] = 0xffff;
    f.pVelX[i] = 0xffff; f.pVelY[i] = 0xffff; f.pVelZ[i] = 0xffff;
    f.pYaw[i] = 0x0fff; f.pStamina[i] = 0xff;
    f.pAnim[i] = 0x3f; f.pAnimBlend[i] = 0xff; f.pSkill[i] = 0x0f;
    f.pAnimExec[i] = 0x0f;
  }
  f.bPosX = 0xffff; f.bPosY = 0xffff; f.bPosZ = 0xffff;
  f.bVelX = 0xffff; f.bVelY = 0xffff; f.bVelZ = 0xffff;
  f.bAngX = 0xffff; f.bAngY = 0xffff; f.bAngZ = 0xffff;
  f.bQuat = 0xffffffff >>> 0; f.bPossessed = 0x3f; f.bGrounded = 1; f.bPowerKick = 1;
  f.mPhase = 0xf; f.mRestartStage = 0x3; f.mHalf = 0x3;
  f.mClock = (1 << WIRE_CLOCK_BITS) - 1; f.mAddedTime = 0xffff;
  f.mScoreA = 0x7f; f.mScoreB = 0x7f; f.mRestartTeam = 0x3;
  f.mRestartX = 0xffff; f.mRestartZ = 0xffff; f.mRestartTaker = 0x3f;
  f.mPossessionTeam = 0x3; f.mAdvantage = (1 << WIRE_ADVANTAGE_BITS) - 1;
  f.mPendingFoulPlayer = 0x3f; f.mPendingFoulSeverity = 0x7; f.mExcitement = 0xff;

  worstCaseWriter.reset();
  encodeFullSnapshot(worstCaseWriter, f, 0xffffffff);
  return worstCaseWriter.finish();
}
